import { useEffect } from "react";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@altimateai/lego";
import { IconCalendar } from "@tabler/icons-react";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { TIME_PERIODS } from "../constants";

const WarehouseDateRange = () => {
  const { getFilterValue, registerFilters } = useDatatableContext();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  // Register date filters
  useEffect(() => {
    registerFilters({
      startDate: {
        label: "Start Date",
        defaultValue: dayjs().subtract(30, "day").toDate(),
      },
      endDate: {
        label: "End Date",
        defaultValue: dayjs().toDate(),
      },
      period: {
        label: "Period",
        defaultValue: 30,
      },
    });
  }, [registerFilters]);

  // Get current date filters (handle both Date objects and ISO strings from search params)
  const startDateValue = getFilterValue("startDate");
  const endDateValue = getFilterValue("endDate");

  const startDate = startDateValue
    ? startDateValue instanceof Date
      ? startDateValue
      : new Date(startDateValue as string)
    : dayjs().subtract(30, "day").toDate();

  const endDate = endDateValue
    ? endDateValue instanceof Date
      ? endDateValue
      : new Date(endDateValue as string)
    : dayjs().toDate();

  // Calculate current period from dates
  const currentPeriod = Math.ceil(dayjs(endDate).diff(dayjs(startDate), "days"));

  // Helper function to set exact period days (always 1, 7, or 30)
  const handlePeriodSelect = (days: number) => {
    const newEndDate = dayjs().toDate();
    const newStartDate = dayjs().subtract(days, "day").toDate();
    updateDateRange(newStartDate, newEndDate);
  };

  const updateDateRange = (newStartDate: Date, newEndDate: Date) => {
    const periodDays = Math.ceil(
      (newEndDate.getTime() - newStartDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Update search params instead of directly updating context
    updateSearchParamsByKey({
      startDate: newStartDate.toISOString(),
      endDate: newEndDate.toISOString(),
      period: periodDays.toString(),
    });
  };

  return (
    <Select
      value={currentPeriod.toString()}
      onValueChange={value => handlePeriodSelect(Number(value))}
    >
      <SelectTrigger className="al-w-[180px] al-bg-white">
        <SelectValue placeholder="Select time period" />
      </SelectTrigger>
      <SelectContent>
        {TIME_PERIODS.map(timePeriod => (
          <SelectItem key={timePeriod.period} value={timePeriod.period.toString()}>
            <Stack align={"center"} gap={"md"}>
              <IconCalendar />
              <span>{timePeriod.label}</span>
            </Stack>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Future: Replace TIME_PERIODS dropdown with DateRange component
  // <DateRange
  //   startDate={startDate}
  //   endDate={endDate}
  //   onDateRangeSelect={updateDateRange}
  // />
};

export default WarehouseDateRange;
